import { FundingApplicationSchemas } from "api-types";
import dayjs from "dayjs";
import { useCallback, useEffect } from "react";
import { useSearchParams } from "react-router";
import { useSpaNavigate } from "shared-ui";
import { useCustomerAppConfig } from "undercarriage";
import { addError } from "@wayflyer/blackbox-fe";

import { AIR_IDENTIFIER_STORAGE_KEY } from "../../components/additional-information-requests/utils";
import { useAirRevenueSourceUpdateMutation } from "../../queries/air-response/air-revenue-source.mutations";
import { usePlatformConnectors } from "../../queries/connectors/queries";
import { ConnectorCategory } from "../../types";

export const platformConnectorsQueryParams = {
  category: ConnectorCategory.ECOMMERCE,
  skipConnections: false,
};

export const useHandleAirConnection = ({ airBasePath }: { airBasePath: string }) => {
  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  const { navigate } = useSpaNavigate("FundingApp");
  const [searchParams, setSearchParams] = useSearchParams();
  const updateAirRevenueSource = useAirRevenueSourceUpdateMutation(customerDomain);

  const { data: storeConnectors } = usePlatformConnectors(customerDomain, platformConnectorsQueryParams);

  const clearAirResponseInFlightData = useCallback(() => {
    setSearchParams({}, { replace: true });
    localStorage.removeItem(AIR_IDENTIFIER_STORAGE_KEY);
  }, [setSearchParams]);

  const submitAIRResponse = useCallback(
    (
      airType: FundingApplicationSchemas.AdditionalInformationRequestType,
      airPublicIdentifier: string,
      airConnectionId: string | number
    ) => {
      switch (airType) {
        default:
          // We only support additional_revenue_source connections for now
          return updateAirRevenueSource.mutateAsync({
            airPublicId: airPublicIdentifier,
            connectionIds: [+airConnectionId],
          });
      }
    },
    [updateAirRevenueSource]
  );

  useEffect(() => {
    // For all connections except woocommerce2, the connectionId is passed in the query params
    // woocommerce can only be connected via AIR/funding application if one does not already exist
    // So if a connections exists for woocommerce, we will use that connectionId
    // This useEffect is in place because we need to wait for the connectionId to be available from the connectors api
    const connectionInProgress = searchParams.get("platform");

    if (connectionInProgress !== "woocommerce2") {
      return;
    }
    if (updateAirRevenueSource.isPending) {
      // prevent submission of AIR response if the update air revenue source mutation is in flight
      return;
    }
    const connectorInProgressConnections = storeConnectors?.find(
      (connector) => connector.slug === connectionInProgress
    )?.connections;

    const connectionId = connectorInProgressConnections?.[0]?.id;
    // wait for connectionId to be available from connectors api
    if (!connectionId) {
      return;
    }
    const airAwaitingResponse = localStorage.getItem(AIR_IDENTIFIER_STORAGE_KEY);
    if (!airAwaitingResponse) {
      return;
    }
    const airAwaitingResponseData = JSON.parse(airAwaitingResponse);
    // if connectionId is available, we can submit the AIR response
    submitAIRResponse(airAwaitingResponseData.type, airAwaitingResponseData.airPublicIdentifier, connectionId)
      .then(clearAirResponseInFlightData)
      .catch(addError);
  }, [
    clearAirResponseInFlightData,
    submitAIRResponse,
    storeConnectors,
    searchParams,
    updateAirRevenueSource.isPending,
  ]);

  const handleSuccess = useCallback(
    async (searchParams: URLSearchParams) => {
      // We need to check if we have an air response "in flight"
      const airAwaitingResponse = localStorage.getItem(AIR_IDENTIFIER_STORAGE_KEY);
      if (!airAwaitingResponse) {
        return;
      }

      const airAwaitingResponseData = JSON.parse(airAwaitingResponse);
      const connectionId = searchParams.get("connection_id");
      const connectionInProgress = searchParams.get("platform");
      const firstConnection = searchParams.get("first-connection");

      if (connectionInProgress === "woocommerce2") {
        // skip woocommerce2 as we will handle it in the useEffect above
        return;
      }

      const airConnectionId = connectionId ?? null;

      if (firstConnection === "False") {
        clearAirResponseInFlightData();
        navigate(
          `${airBasePath}/${airAwaitingResponseData.airPublicIdentifier}?platform=${connectionInProgress}&oauth-error=shop_uniqueness_violation`,
          { replace: true }
        );
        return;
      }

      if (airConnectionId && dayjs().isBefore(dayjs(airAwaitingResponseData.expiry))) {
        await submitAIRResponse(
          airAwaitingResponseData.type,
          airAwaitingResponseData.airPublicIdentifier,
          airConnectionId
        ).catch(addError);

        clearAirResponseInFlightData();
      }
    },
    [clearAirResponseInFlightData, navigate, airBasePath, submitAIRResponse]
  );

  const handleError = useCallback(
    (searchParams: URLSearchParams) => {
      const airAwaitingResponse = localStorage.getItem(AIR_IDENTIFIER_STORAGE_KEY);
      let path = null;

      const connectionInProgress = searchParams.get("platform");
      const oAuthError = searchParams.get("oauth-error");

      // If there was an OAuth error, redirect to in flight air platform with oauth error
      if (!airAwaitingResponse) {
        path = `${airBasePath}?platform=${connectionInProgress}&oauth-error=${oAuthError}`;
      } else {
        const airAwaitingResponseData = JSON.parse(airAwaitingResponse);
        path = `${airBasePath}/${airAwaitingResponseData.airPublicIdentifier}?platform=${connectionInProgress}&oauth-error=${oAuthError}`;
      }

      clearAirResponseInFlightData();
      navigate(path, { replace: true });
      return;
    },
    [navigate, airBasePath, clearAirResponseInFlightData]
  );

  return {
    handleSuccess,
    handleError,
  };
};
